import { CardActions, Grid, IconButton, Paper, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { DeckDTO, TrainingPlan } from '@/generated';
import { PlanCard } from './PlanCard';
import { DefaultContainer } from '@/components/DefaultContainer';
import AddIcon from '@mui/icons-material/Add';
import { Delete, Edit } from '@mui/icons-material';
import { DeleteDialog } from '@/components/DeleteDialog';
import { UpdatePlanModal } from './UpdatePlanModal';
import { isPlanEditor } from './utils';
import { AppContext } from '@/contexts/app.context';
import { useListUserDecksQuery, usePlansQuery } from '@/hooks/query-hooks';
import { useDeletePlan } from '@/hooks/plan-mutation.hooks';

export const Plans = () => {
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState<boolean>(false);
  const { decks } = useListUserDecksQuery(true);

  return (
    <DefaultContainer>
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
          <Grid>
            <Tooltip title={'Create Plan'}>
              <IconButton
                onClick={() => {
                  setCreatePlanDialogOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
      {decks && <PlanList deckList={decks} open={createPlanDialogOpen} setOpen={setCreatePlanDialogOpen} />}
    </DefaultContainer>
  );
};

export const PlanList = (props: { open: boolean; setOpen: (open: boolean) => void; deckList: DeckDTO[] }) => {
  const { planList } = usePlansQuery();

  return (
    <>
      <UpdatePlanModal
        show={props.open}
        setShow={(show) => {
          props.setOpen(show);
        }}
      />
      <Grid container direction={'row'} spacing={1}>
        {planList?.map((p: TrainingPlan, i) => (
          <PlanGridElement deckList={props.deckList} key={p.id} plan={p} />
        ))}
      </Grid>
    </>
  );
};

const PlanGridElement = (props: { deckList: DeckDTO[]; plan: TrainingPlan }) => {
  const { plan } = props;
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [updatePlanDialogOpen, setUpdatePlanDialogOpen] = useState<boolean>(false);
  const deletePlanMutation = useDeletePlan();

  return (
    <>
      <Grid size={{ xs: 12, md: 4 }}>
        <DeleteDialog
          title={`Really delete ${plan.title}`}
          text={`Really delete ${plan.title}`}
          open={showDeleteDialog}
          handleClose={async (deleted) => {
            if (deleted) {
              try {
                await deletePlanMutation.mutateAsync(plan.id);
              } catch (e) {
                console.error(e);
              }
            }
            setShowDeleteDialog(false);
          }}
        />
        <UpdatePlanModal
          plan={plan}
          setShow={(show) => {
            setUpdatePlanDialogOpen(show);
          }}
          show={updatePlanDialogOpen}
        />
        <PlanCard decks={props.deckList} timeout={1000} plan={plan}>
          <CardActions sx={{ justifyContent: 'flex-end', alignSelf: 'flex-end', flex: 0.1 }}>
            {isPlanEditor(plan, localUser) && (
              <>
                <Tooltip title={`Edit ${plan.title}`}>
                  <IconButton
                    onClick={async () => {
                      setUpdatePlanDialogOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Delete ${plan.title}`}>
                  <IconButton
                    onClick={async () => {
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </CardActions>
        </PlanCard>
      </Grid>
    </>
  );
};
